import { Alert, Dialog, FormSection, FormStack, MenuItem, SelectField, Stack, TextField } from '@all-manager/design-system-v2';
import { Controller } from 'react-hook-form';
import { ProviderContactDatabase } from '../../../../types';
import { CIVILITIES, CIVILITY_LABELS, NO_CIVILITY } from '../../constants/civilities';
import useAddUpdateContactModal from './useAddUpdateContactModal';

type Props = {
  onClose: () => void;
  providerId: string;
  contact?: ProviderContactDatabase;
}

export default function AddUpdateContactModal(props: Props) {
  const { form, onSubmit, isSubmitting, serverError } = useAddUpdateContactModal({
    providerId: props.providerId,
    contact: props.contact,
    onClose: props.onClose
  });

  return (
    <Dialog
      open
      onClose={props.onClose}
      onSubmit={onSubmit}
      maxWidth="sm"
      fullWidth
      title={props.contact ? 'Modification du contact' : 'Nouveau contact'}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: props.contact ? 'Modifier' : 'Créer',
          type: 'submit',
          loading: isSubmitting,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isSubmitting,
        },
      }}
    >
      <Stack spacing={3}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <FormStack direction="row">
          <Controller
            control={form.control}
            name="civility"
            render={({ field }) => (
              <SelectField<string>
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={typeof field.value === 'string' ? field.value : NO_CIVILITY}
                label="Civilité"
                helperText="Utilisée pour les courriers et e-mails"
                fullWidth
              >
                <MenuItem value={NO_CIVILITY}>Non précisée</MenuItem>
                {CIVILITIES.map((civility) => (
                  <MenuItem key={civility} value={civility}>{CIVILITY_LABELS[civility]}</MenuItem>
                ))}
              </SelectField>
            )}
          />

          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value ?? ''}
                label="NOM Prénom"
                fullWidth
                required
                autoFocus
                autoComplete="name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

        </FormStack>

        <FormStack direction="row">
          <Controller
            control={form.control}
            name="role"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value ?? ''}
                label="Poste"
                fullWidth
                required
                autoComplete="organization-title"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormStack>

        <FormSection title="Coordonnées">
          <FormStack direction="row">
            <Controller
              control={form.control}
              name="phone1"
              render={({ field, fieldState }) => (
                <TextField
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value ?? ''}
                  label="Téléphone 1"
                  type="tel"
                  fullWidth
                  autoComplete="tel"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              control={form.control}
              name="phone2"
              render={({ field, fieldState }) => (
                <TextField
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  value={field.value ?? ''}
                  label="Téléphone 2"
                  type="tel"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormStack>

          <Controller
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={typeof field.value === 'string' ? field.value : ''}
                label="Email"
                type="email"
                fullWidth
                autoComplete="email"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </FormSection>
      </Stack>
    </Dialog>
  );
}
